'use client';

import { c as _c } from "react/compiler-runtime";
import { type Post } from '@shared/all/domains/posts';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Loader2, Minus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import useContentUIRenderer from '../../../hooks/useContentUIRenderer';
import { client } from '../../../utils/trpc';
import Error from '../../common/Error';
import Modal, { ModalContent, ModalHeader } from '../../HOC/Modal';
import PostDeleteConf from '../PostDeleteConf';
import PostItemBody from '../PostItemBody';
import { formatWhen } from '../../scheduled/ScheduledPostRow';
import type { DraftItemSelectHandler } from './NewPostModal';
type Props = {
  closeHandler: () => void;
  draftItemSelectHandler: (draftItemSelectHandler: DraftItemSelectHandler) => void;
};
const DraftPostsList = props => {
  const $ = _c(43);
  const {
    ref,
    inView
  } = useInView();
  const {
    filterText
  } = useContentUIRenderer();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["drafts"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: t0,
      queryFn: _temp,
      initialPageParam: undefined as string | undefined,
      getNextPageParam: _temp2
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    error
  } = useInfiniteQuery(t1);
  let t2;
  if ($[2] !== fetchNextPage || $[3] !== inView) {
    t2 = () => {
      if (inView) {
        fetchNextPage();
      }
    };
    $[2] = fetchNextPage;
    $[3] = inView;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== inView) {
    t3 = [inView];
    $[5] = inView;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      queryKey: ["scheduledPosts"],
      queryFn: _temp3
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    data: scheduled
  } = useQuery(t4);
  let t5;
  if ($[8] !== scheduled?.posts) {
    t5 = postId => scheduled?.posts.find(p => p.postId === postId)?.scheduledFor;
    $[8] = scheduled?.posts;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const scheduledFor = t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <h2>Drafts</h2>;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== props.closeHandler) {
    t7 = <ModalHeader closeHandler={props.closeHandler}>{t6}</ModalHeader>;
    $[11] = props.closeHandler;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== data?.pages || $[14] !== error || $[15] !== filterText || $[16] !== isLoading || $[17] !== props || $[18] !== scheduledFor) {
    t8 = !isLoading && !error && data?.pages.flatMap(_temp4).map(post => <DraftPostItem key={post.id} post={post} scheduledFor={scheduledFor(post.id)} clickHandler={() => props.draftItemSelectHandler({
      content: filterText({
        content: post.content,
        isFullLength: true
      }),
      factiiiOpen: post.postFactiiisCount.length > 0,
      refDraftPostId: post.id,
      anonymousPost: post.user.anonymous
    })} />);
    $[13] = data?.pages;
    $[14] = error;
    $[15] = filterText;
    $[16] = isLoading;
    $[17] = props;
    $[18] = scheduledFor;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== data?.pages || $[21] !== error || $[22] !== isLoading) {
    t9 = !isLoading && !error && data?.pages.flatMap(_temp5).length === 0 && <p className="m-2 text-center text-textPrimary">No drafts found</p>;
    $[20] = data?.pages;
    $[21] = error;
    $[22] = isLoading;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== data?.pages || $[25] !== hasNextPage) {
    t10 = data?.pages.flatMap(_temp6).length !== 0 && !hasNextPage && <p className="m-2 text-center text-textPrimary">{"You've reached the end. \uD83C\uDF89"}</p>;
    $[24] = data?.pages;
    $[25] = hasNextPage;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== hasNextPage || $[28] !== ref) {
    t11 = hasNextPage && <div className="m-2 flex justify-center text-textPrimary" ref={ref}><Loader2 className="animate-spin" height={50} width={50} /></div>;
    $[27] = hasNextPage;
    $[28] = ref;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== error || $[31] !== isLoading) {
    t12 = <Error error={error} loading={isLoading} />;
    $[30] = error;
    $[31] = isLoading;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t10 || $[34] !== t11 || $[35] !== t12 || $[36] !== t8 || $[37] !== t9) {
    t13 = <ModalContent>{t8}{t9}{t10}{t11}{t12}</ModalContent>;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t8;
    $[37] = t9;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== props.closeHandler || $[40] !== t13 || $[41] !== t7) {
    t14 = <Modal closeHandler={props.closeHandler}>{t7}{t13}</Modal>;
    $[39] = props.closeHandler;
    $[40] = t13;
    $[41] = t7;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  return t14;
};
type DraftPostItemProps = {
  post: Post;
  /** When it goes out, for a draft that is on the schedule. */
  scheduledFor?: Date;
  clickHandler: () => void;
};
function DraftPostItem(props) {
  const $ = _c(5);
  const [deletePostConfOpen, setDeletePostConfOpen] = useState(false);
  const [discarded, setDiscarded] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = discarded_0 => {
      setDiscarded(discarded_0);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const discardPost = t0;
  let t1;
  if ($[1] !== deletePostConfOpen || $[2] !== discarded || $[3] !== props) {
    t1 = discarded ? null : <div className="flex items-center justify-between rounded-md px-2 text-start hover:bg-background-light"><div className="flex-1 cursor-pointer" onClick={() => props.clickHandler()}><PostItemBody post={props.post} nsfw={false} politics={false} troll={false} disableLink={false} isFullLength={false} isInView={true} ignorePostFactiiiIds={[]} /><span className="text-sm text-textSecondary/60">Saved on {new Date(props.post.createdAt).toLocaleDateString()}{" "}{new Date(props.post.createdAt).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit"
          })}</span>{props.scheduledFor && <span className="ml-2 rounded-full border border-primary/40 px-2 py-px text-xs text-primary">Scheduled for {formatWhen(new Date(props.scheduledFor))}</span>}</div><button onClick={() => setDeletePostConfOpen(true)} className="flex items-center justify-center p-2 text-destructive hover:text-destructive"><Minus /></button>{deletePostConfOpen && <PostDeleteConf closeHandler={() => setDeletePostConfOpen(false)} postId={props.post.id} isDraft={true} discardPost={discardPost} draftContent={props.post.content} />}</div>;
    $[1] = deletePostConfOpen;
    $[2] = discarded;
    $[3] = props;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
export default DraftPostsList;
function _temp(t0) {
  const {
    pageParam
  } = t0;
  return unwrapFromResult(client.posts.drafts.query({
    cursor: pageParam
  }));
}
function _temp2(lastPage) {
  const posts = lastPage.posts;
  if (posts.length < 10) {
    return;
  }
  return posts[posts.length - 1]?.id;
}
function _temp3() {
  return unwrapFromResult(client.scheduledPosts.list.query());
}
function _temp4(page) {
  return page.posts;
}
function _temp5(page_0) {
  return page_0.posts;
}
function _temp6(page_1) {
  return page_1.posts;
}